/* ===========================================================================
 * td-add-and-open-drawer.js — one shared add-to-cart → refresh → open helper.
 *
 * Why this exists: several call sites (offer-multi, the inline landing-page
 * buys, the pack-variant forms, the upsell scripts) add to the cart and then
 * do nothing visible. They were written when UpCart was installed and opened
 * its drawer by itself on every /cart/add.js. With the app embed off, the add
 * still succeeds and the customer sees nothing at all — no drawer, no error.
 *
 * The mechanism here is NOT new: it is the block already proven in
 * sections/product-main.liquid (tdOpenCartDrawer / tdApplyDrawerSections),
 * lifted verbatim and given one entry point so the call sites stop each
 * carrying their own copy.
 *
 * Deliberately UpCart-first, exactly like the reference: where UpCart is still
 * enabled this helper hands off to it and changes nothing. That makes every
 * call site using it safe to deploy to LIVE *before* the UpCart switch is
 * thrown — behaviour today is identical, behaviour after the switch is correct.
 *
 * Usage:
 *   tdAddAndOpenDrawer({ id: 123, quantity: 1, properties: {...} })
 *   tdAddAndOpenDrawer({ items: [{ id: 123, quantity: 1 }] })
 *   tdAddAndOpenDrawer(formData)          // a <form action="/cart/add"> FormData
 *   → Promise, resolves with the /cart/add.js body, rejects on a cart error
 *     (err.serverMessage carries Shopify's description; the drawer is NOT
 *     opened on rejection).
 * =========================================================================== */
(function () {
  'use strict';
  if (window.tdAddAndOpenDrawer) return;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';
  }

  // --- Swap the freshly rendered sections into the page. ---------------------
  // Prefer swapping #CartDrawer's innerHTML and keeping that node alive, the
  // same reason cart-upsell.js does it: Dawn's open() resolves
  // getElementById('CartDrawer') on transitionend and must never see null.
  // Falls back to the <cart-drawer> inner swap used by product-main.liquid.
  function applySections(sections) {
    if (sections && sections['cart-drawer']) {
      var tmp = document.createElement('div');
      tmp.innerHTML = sections['cart-drawer'];
      var host = document.querySelector('cart-drawer');
      var freshInner = tmp.querySelector('#CartDrawer');
      var liveInner = host && host.querySelector('#CartDrawer');
      if (freshInner && liveInner) {
        liveInner.innerHTML = freshInner.innerHTML;
      } else {
        var nd = tmp.querySelector('cart-drawer');
        if (host && nd) host.innerHTML = nd.innerHTML;
      }
      if (host) host.classList.remove('is-empty');
    }
    if (sections && sections['cart-icon-bubble']) {
      var bub = document.getElementById('cart-icon-bubble');
      if (bub) bub.innerHTML = sections['cart-icon-bubble'];
    }
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    else document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  // --- Refresh + open. UpCart first, exactly as product-main.liquid does. ----
  function openDrawer(providedSections) {
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
      if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      return;
    }
    // The drawer runtime (assets/cart-drawer.js) owns rendering: sequenced,
    // scroll-preserving, state derived from the markup.
    if (window.TDCart) {
      if (providedSections && providedSections['cart-drawer']) window.TDCart.applySections(providedSections);
      else window.TDCart.refresh();
      window.TDCart.open();
      return;
    }
    // Prefer sections returned by /cart/add.js (rendered against the
    // just-updated cart — no cache, no race). Fall back to a no-store fetch.
    if (providedSections && providedSections['cart-drawer']) {
      applySections(providedSections);
      return;
    }
    var sUrl = (rootUrl() + '?sections=cart-drawer,cart-icon-bubble').replace('//?', '/?');
    fetch(sUrl, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(applySections)
      .catch(function () {});
  }

  // Refresh the drawer without adding anything — for the /cart/update.js and
  // /cart/change.js callers (cascade, upsell-blocker) that only mutate lines.
  // `open` defaults to false: a background reconciliation must not pop a drawer
  // in the customer's face.
  function refreshDrawer(open) {
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
      if (open && typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      return Promise.resolve();
    }
    if (window.TDCart) return window.TDCart.refresh({ open: !!open });
    var sUrl = (rootUrl() + '?sections=cart-drawer,cart-icon-bubble').replace('//?', '/?');
    return fetch(sUrl, { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var wasOpen = !!document.querySelector('cart-drawer.active');
        if (sections && sections['cart-drawer']) {
          var tmp = document.createElement('div');
          tmp.innerHTML = sections['cart-drawer'];
          var host = document.querySelector('cart-drawer');
          var freshInner = tmp.querySelector('#CartDrawer');
          var liveInner = host && host.querySelector('#CartDrawer');
          if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
          else { var nd = tmp.querySelector('cart-drawer'); if (host && nd) host.innerHTML = nd.innerHTML; }
        }
        if (sections && sections['cart-icon-bubble']) {
          var bub = document.getElementById('cart-icon-bubble');
          if (bub) bub.innerHTML = sections['cart-icon-bubble'];
        }
        if (open && !wasOpen) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      })
      .catch(function () {});
  }

  // --- Build the request. ----------------------------------------------------
  // A FormData goes out as multipart (browser sets the boundary); anything else
  // goes out as JSON. Both carry the same section list.
  function buildRequest(input) {
    var SECTIONS = 'cart-drawer,cart-icon-bubble';
    var url = window.location.pathname;

    if (typeof FormData !== 'undefined' && input instanceof FormData) {
      input.append('sections', SECTIONS);
      input.append('sections_url', url);
      return { body: input, headers: { Accept: 'application/json' } };
    }

    var payload;
    if (input && input.items) {
      payload = { items: input.items };
    } else if (input && (input.id || input.quantity || input.properties)) {
      payload = {
        items: [{
          id: Number(input.id),
          quantity: Number(input.quantity) || 1,
          properties: input.properties || undefined
        }]
      };
    } else {
      payload = { items: [] };
    }
    payload.sections = SECTIONS;
    payload.sections_url = url;
    return {
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    };
  }

  // --- Public entry point. ---------------------------------------------------
  function tdAddAndOpenDrawer(input) {
    // Custom drawer: the runtime opens the drawer with a skeleton line at once and
    // swaps in the server render when the add lands (same promise contract).
    if (window.TDCart && typeof window.upcartRefreshCart !== 'function') {
      return window.TDCart.add(input);
    }
    var req;
    try {
      req = buildRequest(input);
    } catch (e) {
      console.error('[td-add] bad input', e);
      return Promise.reject(e);
    }

    return fetch(rootUrl().replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: req.headers,
      body: req.body
    })
      .then(function (res) {
        if (!res.ok) {
          // Sold out, quantity rule, invalid variant… Surface Shopify's own
          // message and do NOT open the drawer.
          return res.json().catch(function () { return null; }).then(function (body) {
            var msg = (body && (body.description || body.message)) || 'Cart add failed';
            var err = new Error(msg);
            err.serverMessage = msg;
            err.status = res.status;
            throw err;
          });
        }
        return res.json();
      })
      .then(function (data) {
        document.dispatchEvent(new CustomEvent('tastydose:cart-updated'));
        openDrawer(data && data.sections);
        return data;
      })
      .catch(function (err) {
        // No drawer on failure. Callers that own a visible error element handle
        // the rejection themselves; this is the last-resort report.
        console.error('[td-add] add to cart failed:', (err && err.serverMessage) || err);
        throw err;
      });
  }

  // --- Keep click-outside-to-close alive across section swaps ---------------
  // Dawn binds the overlay's close handler in the CartDrawer constructor and
  // again inside its own renderContents(). Every section swap in this file and
  // in cart-upsell.js replaces #CartDrawer's innerHTML, which destroys the
  // overlay node and that listener with it — after an upsell add, clicking
  // outside silently stopped closing the drawer. Delegated on document, so it
  // can never go stale no matter how often the drawer re-renders.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || t.id !== 'CartDrawer-Overlay') return;
    var drawer = t.closest ? t.closest('cart-drawer') : null;
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  window.tdAddAndOpenDrawer = tdAddAndOpenDrawer;
  window.tdRefreshCartDrawer = refreshDrawer;
})();
